import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { openAppPathInNewTab } from '../utils/app-navigation';
import { entityDetailHref, type RelatedEntity } from '../utils/entity-utils';
import {
  fetchMentionHoverPreview,
  shouldShowRelevance,
  type MentionHoverPreview,
} from '../utils/mention-hover-preview';
import { formatTimelineDate } from '../utils/timeline-date-format';
import { toBcp47Locale } from '../locales/locale';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import { TAG_BADGE_STYLE } from '../utils/tag-badge-style';
import type { MentionHoverTarget } from '../hooks/useMentionHover';
import { MarkHoverCardShell } from './MarkHoverCardShell';
import { EntityRelevanceDisplay } from './EntityRelevanceDisplay';
import { EntityTypeIcon, getEntityTypeLabelKey } from './EntityTypeIcon';
import { RelatedEntityLink } from './RelatedEntityLink';
import { SlidingExternalLinkIcon } from './SlidingExternalLinkIcon';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ButtonTooltip } from './ui/tooltip';

const REFERENCED_IN_PREVIEW_LIMIT = 5;

const TIMELINE_ITEM_TYPE_LABEL_KEYS: Record<string, string> = {
  historical_event: 'timeline_item_type_historical_event',
  fictional_event: 'timeline_item_type_fictional_event',
  planning: 'timeline_item_type_planning',
  other: 'timeline_item_type_other',
  auto_historical: 'timeline_item_type_auto_historical',
  auto_fictional: 'timeline_item_type_auto_fictional',
};

type MentionHoverCardProps = {
  hovered: MentionHoverTarget | null;
  hoverCardContentRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onHoverCardMouseEnter: () => void;
  onHoverCardMouseLeave: () => void;
};

function MentionHoverHtml({ html }: { html: string }): JSX.Element {
  return (
    <div
      className={`line-clamp-3 text-sm text-muted-foreground ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function MentionHoverReferencedIn({
  entities,
  projectId,
}: {
  entities: RelatedEntity[];
  projectId: string;
}): JSX.Element {
  const visible = entities.slice(0, REFERENCED_IN_PREVIEW_LIMIT);
  const remaining = entities.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((entity) => (
        <RelatedEntityLink
          key={`${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
          entity={entity}
          projectId={projectId}
        />
      ))}
      {remaining > 0 ? (
        <span className="text-xs text-muted-foreground">+{remaining}</span>
      ) : null}
    </div>
  );
}

function formatShortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString();
}

function MentionHoverTagList({ tags }: { tags: string[] }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          className="rounded-full px-2 py-0.5 text-xs"
          style={TAG_BADGE_STYLE}
          title={tag}
        >
          {tag}
        </Badge>
      ))}
    </div>
  );
}

function MentionHoverPreviewFields({
  preview,
  projectId,
  t,
  dateLocale,
}: {
  preview: MentionHoverPreview;
  projectId: string;
  t: (key: string) => string;
  dateLocale: string;
}): JSX.Element {
  switch (preview.type) {
    case 'character':
      return (
        <>
          {shouldShowRelevance(preview.relevance) ? (
            <EntityRelevanceDisplay value={preview.relevance} showTooltip={false} />
          ) : null}
          {preview.role ? (
            <span className="text-sm text-muted-foreground">{preview.role}</span>
          ) : null}
          {preview.descriptionHtml ? (
            <MentionHoverHtml html={preview.descriptionHtml} />
          ) : null}
          {preview.referencedIn.length > 0 ? (
            <MentionHoverReferencedIn entities={preview.referencedIn} projectId={projectId} />
          ) : null}
        </>
      );
    case 'location':
      return (
        <>
          {shouldShowRelevance(preview.relevance) ? (
            <EntityRelevanceDisplay value={preview.relevance} showTooltip={false} />
          ) : null}
          {preview.narrativeFunction ? (
            <span className="text-sm text-muted-foreground">{preview.narrativeFunction}</span>
          ) : null}
          {preview.descriptionHtml ? (
            <MentionHoverHtml html={preview.descriptionHtml} />
          ) : null}
        </>
      );
    case 'manual_chapter':
      return (
        <>
          {preview.wordsGoal != null ? (
            <span className="text-sm text-muted-foreground">
              {preview.wordsGoal.toLocaleString()} {t('word_plural')}
            </span>
          ) : null}
          {preview.summary ? (
            <p className="line-clamp-3 text-sm text-muted-foreground">{preview.summary}</p>
          ) : null}
        </>
      );
    case 'storyline':
    case 'scene':
      return preview.descriptionHtml ? (
        <MentionHoverHtml html={preview.descriptionHtml} />
      ) : (
        <></>
      );
    case 'timeline_item': {
      const startLabel =
        preview.startDate && preview.startPrecision
          ? formatTimelineDate(
              preview.startDate,
              preview.startPrecision,
              dateLocale,
              preview.startTimezone
            )
          : null;
      const endLabel =
        preview.endDate && preview.endPrecision
          ? formatTimelineDate(
              preview.endDate,
              preview.endPrecision,
              dateLocale,
              preview.endTimezone
            )
          : null;
      const dateRange =
        startLabel && endLabel
          ? `${startLabel} – ${endLabel}`
          : startLabel || endLabel;
      const itemTypeLabel = preview.itemType
        ? t(TIMELINE_ITEM_TYPE_LABEL_KEYS[preview.itemType] ?? preview.itemType)
        : null;
      return (
        <>
          {dateRange ? (
            <span className="text-sm text-muted-foreground">{dateRange}</span>
          ) : null}
          {itemTypeLabel ? (
            <span className="text-sm text-muted-foreground">{itemTypeLabel}</span>
          ) : null}
          {preview.descriptionHtml ? (
            <MentionHoverHtml html={preview.descriptionHtml} />
          ) : null}
          {preview.referencedIn.length > 0 ? (
            <MentionHoverReferencedIn entities={preview.referencedIn} projectId={projectId} />
          ) : null}
          {preview.tags.length > 0 ? <MentionHoverTagList tags={preview.tags} /> : null}
        </>
      );
    }
    case 'resource':
      return (
        <>
          {preview.tags.length > 0 ? <MentionHoverTagList tags={preview.tags} /> : null}
          {preview.resourceDate ? (
            <span className="text-sm text-muted-foreground">{preview.resourceDate}</span>
          ) : null}
          {preview.createdAt ? (
            <span className="text-sm text-muted-foreground">
              {formatShortDate(preview.createdAt)}
            </span>
          ) : null}
        </>
      );
    case 'document': {
      const created = preview.createdAt ? formatShortDate(preview.createdAt) : null;
      const updated = preview.updatedAt ? formatShortDate(preview.updatedAt) : null;
      const dates =
        created && updated && created !== updated
          ? `${created} · ${updated}`
          : updated || created;
      return dates ? (
        <span className="text-sm text-muted-foreground">{dates}</span>
      ) : (
        <></>
      );
    }
    default:
      return <></>;
  }
}

export const MentionHoverCard: React.FC<MentionHoverCardProps> = ({
  hovered,
  hoverCardContentRef,
  onClose,
  onHoverCardMouseEnter,
  onHoverCardMouseLeave,
}) => {
  const { projectId } = useParams<{ projectId?: string }>();
  const { token, masterKey } = useAuth();
  const { t, locale } = useLocale();
  const [preview, setPreview] = useState<MentionHoverPreview | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hovered || !projectId) {
      setPreview(null);
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    setLoading(true);
    setPreview({
      type: hovered.entity.type === 'user' ? 'user' : 'unknown',
      title: hovered.entity.label || hovered.entity.id,
    });

    void fetchMentionHoverPreview({
      entity: hovered.entity,
      projectId,
      token,
      masterKey,
      signal: abortController.signal,
    })
      .then((nextPreview) => {
        if (!abortController.signal.aborted) {
          setPreview(nextPreview);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [hovered, projectId, token, masterKey]);

  if (!hovered || !projectId) {
    return null;
  }

  const title = preview?.title || hovered.entity.label || hovered.entity.id;
  const typeLabel = t(
    hovered.entity.type === 'user'
      ? 'mention_is_shared_user'
      : getEntityTypeLabelKey(hovered.entity.type)
  );
  const href = entityDetailHref(hovered.entity, projectId);
  const dateLocale = toBcp47Locale(locale);

  return (
    <MarkHoverCardShell
      hoveredPosition={hovered.position}
      hoverCardContentRef={hoverCardContentRef}
      onClose={onClose}
      onMouseEnter={onHoverCardMouseEnter}
      onMouseLeave={onHoverCardMouseLeave}
      contentClassName="mention-hover-card w-80 max-w-[min(20rem,calc(100vw-1rem))] p-3"
    >
      <div className="flex flex-col gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <EntityTypeIcon type={hovered.entity.type} typeLabel={typeLabel} className="mt-0.5 h-4 w-4" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium leading-snug text-foreground">{title}</div>
            <div className="text-xs text-muted-foreground">{typeLabel}</div>
          </div>
        </div>

        {preview && preview.type !== 'unknown' && preview.type !== 'user' ? (
          <div className={`flex flex-col gap-2.5 ${loading ? 'opacity-70' : ''}`}>
            <MentionHoverPreviewFields
              preview={preview}
              projectId={projectId}
              t={t}
              dateLocale={dateLocale}
            />
          </div>
        ) : null}

        {href ? (
          <div className="flex justify-end">
            <ButtonTooltip tooltip={t('mention_open_entity_detail_tooltip')} side="top">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="group h-7 w-7"
                aria-label={t('mention_open_entity_detail_tooltip')}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openAppPathInNewTab(href);
                }}
              >
                <SlidingExternalLinkIcon />
              </Button>
            </ButtonTooltip>
          </div>
        ) : null}
      </div>
    </MarkHoverCardShell>
  );
};
